import { randomShuffle } from '@/shared/utils/shuffle';

/**
 * 정답을 드러내지 않는 보기 셔플 — 정책 층.
 *
 * 보기 순서가 곧 정답 정보인 유형(짝맞추기·순서 맞추기 등)은 그냥 섞으면 우연히 정답 배치가
 * 나올 수 있다(2개면 50%). 유형마다 다른 건 "어떤 배치를 정답 노출로 볼 것인가"뿐이라,
 * 그 판정(`reveals`)과 결정적 대체 배치(`fallback`)만 받아 거부 샘플링한다. 허용 배치 중에서
 * 균등하게 뽑히고, 시도가 모두 실패했을 때만 `fallback` 을 쓴다.
 *
 * 순열 자체(무작위·시드)는 `@/shared/utils/shuffle` 이 맡는다. 이 모듈은 그 결과를 거르기만 한다.
 *
 * 값 비교는 `===` 다. id 문자열 같은 원시값이나 같은 참조를 넘긴다.
 * 캐시·시도별 고정(attemptId 등)은 호출부 몫이다 — 이 모듈은 순수 함수만 둔다.
 */
export interface AnswerHidingShuffleOptions<T> {
  /**
   * 한 번 섞는 방법. 기본은 `randomShuffle`.
   * SSR 과 클라이언트가 같은 결과를 내야 하면 `seededShuffle` 을 넘긴다.
   */
  shuffle?: (items: readonly T[]) => T[];
  /**
   * 섞기 시도 횟수. 허용 배치 비율이 1/3 이상인 판정(아래 두 정책)을 가정한 값이다 —
   * 그보다 드문 배치만 허용하는 `reveals` 를 넘기면 fallback 빈도가 커진다.
   * 결과가 고정인 `shuffle` 을 넘길 때는 다시 섞어도 같으므로 1 을 준다.
   */
  maxAttempts?: number;
}

/**
 * 완전순열(정책 B)의 허용 비율이 가장 낮은 경우는 3개(2/6 = 1/3)다. 50번 연속 실패할 확률은
 * (2/3)^50 ≈ 1.6e-9 라 fallback 은 사실상 타지 않는다.
 */
const DEFAULT_MAX_ATTEMPTS = 50;

export const shuffleUntilHidden = <T>(
  items: readonly T[],
  {
    reveals,
    fallback,
    shuffle = randomShuffle,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
  }: AnswerHidingShuffleOptions<T> & {
    reveals: (shuffled: readonly T[]) => boolean;
    /** 시도가 모두 실패했을 때 쓰는 배치. `reveals` 가 false 여야 한다. */
    fallback: () => T[];
  },
): T[] => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const shuffled = shuffle(items);
    if (!reveals(shuffled)) return shuffled;
  }
  return fallback();
};

const rotateByOne = <T>(items: readonly T[]): T[] => [
  ...items.slice(1),
  ...items.slice(0, 1),
];

const isSameOrder = <T>(a: readonly T[], b: readonly T[]) =>
  a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * 정책 A — 전체 순서만 원래와 다르게 섞는다. 일부 항목은 제자리에 남을 수 있다.
 *
 * 순서 맞추기처럼 "원래 순서 그대로"일 때만 정답이 드러나는 유형용이다. 원래 순서가 나오면
 * 다시 섞고, 끝까지 같으면 한 칸 민다.
 *
 * `minItems` 미만이면 판정 없이 한 번만 섞는다. 화면마다 정책이 다르다 — 매번 다시 섞는
 * 플레이는 "2개면 항상 반대"가 학습되므로 3, 한 번 고정해 보여 주는 상세·인쇄물은 2.
 */
export const shuffleToDifferentOrder = <T>(
  items: readonly T[],
  {
    minItems = 2,
    shuffle = randomShuffle,
    maxAttempts,
  }: AnswerHidingShuffleOptions<T> & { minItems?: number } = {},
): T[] => {
  if (items.length < minItems) return shuffle(items);

  return shuffleUntilHidden(items, {
    shuffle,
    maxAttempts,
    reveals: shuffled => isSameOrder(shuffled, items),
    fallback: () => rotateByOne(items),
  });
};

/**
 * 정책 B — `reference[i]` 와 같은 값이 i 번째에 하나도 남지 않게 섞는다(완전순열).
 *
 * 짝맞추기처럼 "같은 줄에 남은 항목 하나하나가 정답"인 유형용이다. 2개면 허용 배치가 엇갈림
 * 하나뿐이라 항상 그 배치가 된다.
 *
 * 완전순열이 정의되지 않는 입력 — 2개 미만, 중복 값, `items` 와 `reference` 의 값 집합이
 * 다름 — 은 판정 없이 한 번만 섞는다.
 */
export const shuffleWithoutFixedPositions = <T>(
  items: readonly T[],
  reference: readonly T[],
  { shuffle = randomShuffle, maxAttempts }: AnswerHidingShuffleOptions<T> = {},
): T[] => {
  const itemSet = new Set(items);
  const canDerange =
    items.length >= 2 &&
    itemSet.size === items.length &&
    reference.length === items.length &&
    reference.every(value => itemSet.has(value)) &&
    new Set(reference).size === reference.length;
  if (!canDerange) return shuffle(items);

  return shuffleUntilHidden(items, {
    shuffle,
    maxAttempts,
    reveals: shuffled =>
      shuffled.some((item, index) => item === reference[index]),
    // 값이 서로 다르고 집합이 같으면, 정답 배치를 한 칸 민 배치는 모든 값이 제자리를 벗어난다.
    fallback: () => rotateByOne(reference),
  });
};
